var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Color, DirectionalLight, AmbientLight, OrthographicCamera, Scene, WebGLRenderer, Vector3, Quaternion, Euler, Box3, Mesh, Sphere, Matrix4, } from 'three';
import { OrbitControls } from './Controls/OrbitControls';
import { TransformControls } from './Controls/TransformControls';
import { EventEmitter } from './EventEmitter';
import { Grid } from './Grid';
import { HistoryEvents, HistoryManager } from './History/History';
import { AddObjectCommand, ChangeVisibilityCommand, MoveObjectsCommand, RemoveObjectCommand, SetPositionCommand, SetRotationCommand, SetScaleCommand } from './History/Commands';
import { isGroup, isLineSegments, isMesh, isObject3D } from './utils/typeUtils';
import { generateUUIDFromGeometryAndHierarchy } from './utils/UuidUtils';
import { CameraAnimator } from './Animation/CameraAnimator';
import { isPart3D, ObjectDisplayMode } from './Objects/Part3D';
import { Loader } from './Loaders/Loader';
const ADDITIONAL_RADIUS_COEFFICIENT = 1.7;
const DEFAULT_PRIMARY_GRID_SIZE = 10;
const DEFAULT_GRID_DIVISIONS = 20;
const DEFAULT_PRIMARY_CELLS_COUNT = 20;
const DEFAULT_NEW_MODEL_SHIFT = 0.5;
const FAR_MULTIPLIER = 20;
const ZOOM_MULTIPLIER = 10;
const MAX_DISTANCE = 5000;
const DEFAULT_DISTANCE = MAX_DISTANCE / ZOOM_MULTIPLIER;
const BASE_FRUSTUM_SIZE = 40;
export var ViewerEvents;
(function (ViewerEvents) {
    ViewerEvents["Initialized"] = "initialized";
    ViewerEvents["AddObjects"] = "addObjects";
    ViewerEvents["RemoveObjects"] = "removeObjects";
    ViewerEvents["SetObjectsPosition"] = "setObjectsPosition";
    ViewerEvents["SetObjectsRotation"] = "setObjectsRotation";
    ViewerEvents["SetObjectsScale"] = "setObjectsScale";
    ViewerEvents["IntersectionFound"] = "intersectionFound";
    ViewerEvents["ObjectTransformChanged"] = "objectTransformChanged";
    ViewerEvents["ObjectsSelected"] = "objectsSelected";
    ViewerEvents["ObjectsSelectedFromViewer"] = "objectsSelectedFromViewer";
    ViewerEvents["CameraChanged"] = "cameraChanged";
    ViewerEvents["TransformControlsModeChanged"] = "transformControlsModeChanged";
    ViewerEvents["ObjectDisplayModeChanged"] = "objectDisplayModeChanged";
    ViewerEvents["ObjectsVisibilityChanged"] = "objectsVisibilityChanged";
    ViewerEvents["MouseDown"] = "mouseDown";
    ViewerEvents["MouseMove"] = "mouseMove";
    ViewerEvents["MouseUp"] = "mouseUp";
    ViewerEvents["SceneGraphChanged"] = "sceneGraphChanged";
    ViewerEvents["Cleared"] = "cleared";
})(ViewerEvents || (ViewerEvents = {}));
export class Viewer extends EventEmitter {
    constructor(canvasId) {
        super();
        this.animationFrameRequestID = -1;
        // renderer init
        const rendererDomElement = this.getRendererDomElement(canvasId);
        if (!rendererDomElement) {
            throw Error(`Canvas with id ${canvasId} not found`);
        }
        this.renderer = new WebGLRenderer({
            canvas: rendererDomElement,
            antialias: true,
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        // canvas resizing
        const canvasParent = rendererDomElement.parentElement;
        if (!canvasParent) {
            throw Error('Canvas has no parent element');
        }
        this.canvasParent = canvasParent;
        window.addEventListener('resize', this.onResize.bind(this), false);
        // scene init
        this.scene = new Scene();
        this.scene.background = new Color(0xffffff);
        // camera init
        const width = this.canvasParent.offsetWidth || window.innerWidth;
        const height = this.canvasParent.offsetHeight || window.innerHeight;
        const aspect = height > 0 ? width / height : 1;
        this.camera = new OrthographicCamera((-BASE_FRUSTUM_SIZE * aspect) / 2, (BASE_FRUSTUM_SIZE * aspect) / 2, BASE_FRUSTUM_SIZE / 2, -BASE_FRUSTUM_SIZE / 2, 0.1, DEFAULT_DISTANCE * FAR_MULTIPLIER);
        this.camera.up.set(0, 0, 1);
        this.camera.position.set(44, 30, 15); // Position camera to view 48ft truck
        this.camera.lookAt(24, 0, 0); // Point camera at truck center (48ft/2 = 24ft)
        this.scene.add(this.camera);
        // controls init
        this.initCameraControls();
        this.initTransformControls();
        this.grid = new Grid(this, DEFAULT_PRIMARY_GRID_SIZE, DEFAULT_GRID_DIVISIONS, DEFAULT_PRIMARY_CELLS_COUNT);
        // this.scene.add(this.grid);
        //misc init
        this.history = new HistoryManager();
        this.history.subscribe(HistoryEvents.historyChanged, this.onHistoryChanged.bind(this));
        this.cameraAnimator = new CameraAnimator(this.camera, this.orbitControls);
        this.initLights();
        // keyboard events
        document.addEventListener('keydown', this.onKeyDown.bind(this));
        //call onResize once to resize canvas to size of the parent
        this.onResize();
        console.log('Viewer created');
        this.emit(ViewerEvents.Initialized, true);
    }
    initLights() {
        this.scene.add(new AmbientLight(0xffffff, 0.55));
        const sun = new DirectionalLight(0xffffff, 1.0);
        sun.position.set(50, -25, 60);
        this.scene.add(sun);
        const fill = new DirectionalLight(0x99bbff, 0.35);
        fill.position.set(-30, 40, 15);
        this.scene.add(fill);
    }
    initCameraControls() {
        this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement);
        this.orbitControls.enableDamping = true;
        this.orbitControls.dampingFactor = 0.07;
        this.orbitControls.screenSpacePanning = true;
        this.orbitControls.minZoom = 0.05;
        this.orbitControls.maxZoom = 50;
        this.orbitControls.minDistance = 1;
        this.orbitControls.maxDistance = MAX_DISTANCE;
        this.orbitControls.target.set(24, 0, 0);
        this.orbitControls.update();
        this.orbitControls.addEventListener('change', () => {
            this.emitCameraChangedEvent();
        });
    }
    initTransformControls() {
        this.transformControls = new TransformControls(this.camera, this.renderer.domElement, () => []);
        this.scene.add(this.transformControls);
        let objectPositionsOnMouseDown = [];
        let objectRotationsOnMouseDown = [];
        let objectScalesOnMouseDown = [];
        this.transformControls.addEventListener('mouseDown', () => {
            const objects = [];
            if (objects) {
                objectPositionsOnMouseDown = objects.map((object) => object.position.clone());
                objectRotationsOnMouseDown = objects.map((object) => object.quaternion.clone());
                objectScalesOnMouseDown = objects.map((object) => object.scale.clone());
            }
        });
        this.transformControls.addEventListener('mouseUp', () => {
            const objects = [];
            if (objects.length) {
                switch (this.transformControls.getMode()) {
                    case 'translate': // TODO: change literals to enum?
                        if (objectPositionsOnMouseDown.length && !objectPositionsOnMouseDown[0].equals(objects[0].position)) {
                            const wireFrames = this.getWireFrameObjectsByParts3d(objects);
                            const objectsPosition = objects.map((o) => o.position);
                            this.execute(new SetPositionCommand(this, [...wireFrames, ...objects], [...objectsPosition, ...objectsPosition], [...objectPositionsOnMouseDown, ...objectPositionsOnMouseDown]));
                            this.emit(ViewerEvents.ObjectTransformChanged, '');
                        }
                        break;
                    case 'rotate':
                        if (objectRotationsOnMouseDown.length && !objectRotationsOnMouseDown[0].equals(objects[0].quaternion)) {
                            const wireFrames = this.getWireFrameObjectsByParts3d(objects);
                            const objectsRotation = objects.map((o) => o.quaternion);
                            this.execute(new SetRotationCommand(this, [...wireFrames, ...objects], [...objectsRotation, ...objectsRotation], [...objectRotationsOnMouseDown, ...objectRotationsOnMouseDown]));
                        }
                        break;
                    case 'scale':
                        if (objectScalesOnMouseDown.length && !objectScalesOnMouseDown[0].equals(objects[0].scale)) {
                            const wireFrames = this.getWireFrameObjectsByParts3d(objects);
                            const objectsScale = objects.map((o) => o.scale);
                            this.execute(new SetScaleCommand(this, [...wireFrames, ...objects], [...objectsScale, ...objectsScale], [...objectScalesOnMouseDown, ...objectScalesOnMouseDown]));
                        }
                        break;
                }
            }
        });
        this.transformControls.addEventListener('dragging-changed', (event) => {
            this.orbitControls.enabled = !event.value;
        });
        this.transformControls.addEventListener('objectChange', (event) => {
            if (event.target.object) {
                this.emit(ViewerEvents.ObjectTransformChanged, event.target.object.uuid);
            }
        });
        this.transformControls.addEventListener('mode-changed', (event) => {
            this.emit(ViewerEvents.TransformControlsModeChanged, event.value);
        });
    }
    dispose() {
        this.stopAnimate();
        this.scene.traverse((child) => {
            if (child instanceof Mesh) {
                child.geometry.dispose();
                child.material.dispose();
            }
            else if (child instanceof OrbitControls || child instanceof TransformControls) {
                child.dispose();
            }
        });
        this.renderer.dispose();
    }
    getObjectByUUID(uuid) {
        return this.scene.getObjectByProperty('uuid', uuid);
    }
    removeObjectByUUID(uuid) {
        const object = this.getObjectByUUID(uuid);
        if (object) {
            this.scene.remove(object);
        }
    }
    getObjectTransformByUUID(uuid) {
        const object = this.getObjectByUUID(uuid);
        if (!object) {
            return null;
        }
        return {
            position: {
                x: object.position.x,
                y: object.position.y,
                z: object.position.z,
            },
            rotation: {
                x: object.rotation.x,
                y: object.rotation.y,
                z: object.rotation.z,
            },
            scale: {
                x: object.scale.x,
                y: object.scale.y,
                z: object.scale.z,
            }
        };
    }
    applyObjectTransformByUUID(uuid, transforms) {
        const object = this.getObjectByUUID(uuid);
        if (!object) {
            return false;
        }
        const { position, rotation, scale } = transforms;
        const positionVector = new Vector3(position.x, position.y, position.z);
        const rotationQuaternion = new Quaternion().setFromEuler(new Euler(rotation.x, rotation.y, rotation.z));
        const scaleVector = new Vector3(scale.x, scale.y, scale.z);
        object.matrix.compose(positionVector, rotationQuaternion, scaleVector);
        return true;
    }
    applyObjectPositionByUUID(uuid, position) {
        const object = this.getObjectByUUID(uuid);
        if (object) {
            const newPosition = new Vector3(position.x, position.y, position.z);
            const wireFrames = this.getWireFrameObjectsByPart3d(object);
            const wireFramePosition = wireFrames.map(() => (newPosition));
            this.execute(new SetPositionCommand(this, [...wireFrames, object], [...wireFramePosition, newPosition]));
        }
    }
    applyObjectRotationByUUID(uuid, rotation) {
        const object = this.getObjectByUUID(uuid);
        if (object) {
            const newRotation = new Quaternion().setFromEuler(new Euler(rotation.x, rotation.y, rotation.z));
            const wireFrames = this.getWireFrameObjectsByPart3d(object);
            const wireFrameRotation = wireFrames.map(() => (newRotation));
            this.execute(new SetRotationCommand(this, [...wireFrames, object], [...wireFrameRotation, newRotation]));
        }
    }
    applyObjectScaleByUUID(uuid, scale) {
        const object = this.getObjectByUUID(uuid);
        if (object) {
            const newScale = new Vector3(scale.x, scale.y, scale.z);
            const wireFrames = this.getWireFrameObjectsByPart3d(object);
            const wireFrameScale = wireFrames.map(() => (newScale));
            this.execute(new SetScaleCommand(this, [...wireFrames, object], [...wireFrameScale, newScale]));
        }
    }
    selectObjectByUUID(uuid, multiple = false) {
        // No selection manager - method kept for API compatibility
    }
    deselectObjectByUUID(uuid) {
        // No selection manager - method kept for API compatibility
    }
    moveObjectToParent(objectUUID, parentUUID) {
        const object = this.getObjectByUUID(objectUUID);
        if (!object) {
            return;
        }
        const parent = this.getObjectByUUID(parentUUID);
        if (!parent) {
            return;
        }
        let isParentDescendant = false;
        parent.traverseAncestors((ancestor) => {
            if (ancestor === object) {
                isParentDescendant = true;
            }
        });
        if (isParentDescendant) {
            return;
        }
        const wireFrames = this.getWireFrameObjectsByPart3d(object);
        this.execute(new MoveObjectsCommand([...wireFrames, object], this, parent));
    }
    removeObjectFromParent(objectUUID) {
        const object = this.getObjectByUUID(objectUUID);
        if (!object) {
            return;
        }
        const wireFrames = this.getWireFrameObjectsByPart3d(object);
        this.execute(new RemoveObjectCommand([...wireFrames, object], this));
    }
    getObjectInfoByUUID(uuid) {
        var _a;
        const object = this.getObjectByUUID(uuid);
        let verticesCount = 0;
        let trianglesCount = 0;
        if (object) {
            object.traverse((child) => {
                if (isMesh(child)) {
                    verticesCount += child.geometry.attributes.position.count;
                    if (child.geometry.index) {
                        trianglesCount += child.geometry.index.count / 3;
                    }
                    else {
                        trianglesCount += child.geometry.attributes.position.count / 3;
                    }
                }
            });
        }
        return {
            uuid: (_a = object === null || object === void 0 ? void 0 : object.uuid) !== null && _a !== void 0 ? _a : '',
            verticesCount,
            trianglesCount,
        };
    }
    getWireFrameObjectsByPart3d(part) {
        var _a;
        const wireFrames = [];
        if ((_a = part.userData) === null || _a === void 0 ? void 0 : _a.wireframeUUID) {
            const wireFrameObject = this.getObjectByUUID(part.userData.wireframeUUID);
            if (wireFrameObject) {
                wireFrames.push(wireFrameObject);
            }
        }
        return wireFrames;
    }
    getWireFrameObjectsByParts3d(parts) {
        const wireFrames = [];
        for (const part of parts) {
            const result = this.getWireFrameObjectsByPart3d(part);
            wireFrames.push(...result);
        }
        return wireFrames;
    }
    setZoom(zoomFactor) {
        this.camera.zoom = zoomFactor;
        this.camera.updateProjectionMatrix();
        this.emitCameraChangedEvent();
    }
    zoomIn() {
        this.camera.zoom = Math.min(this.camera.zoom * 1.2, this.orbitControls.maxZoom || 50);
        this.camera.updateProjectionMatrix();
        this.orbitControls.update();
        this.emitCameraChangedEvent();
    }
    zoomOut() {
        this.camera.zoom = Math.max(this.camera.zoom / 1.2, this.orbitControls.minZoom || 0.05);
        this.camera.updateProjectionMatrix();
        this.orbitControls.update();
        this.emitCameraChangedEvent();
    }
    zoomToSphere(sphere) {
        if (sphere.radius <= 0)
            return;
        const aspect = (this.camera.right - this.camera.left) / (this.camera.top - this.camera.bottom) || 1;
        const baseFrustumWidth = BASE_FRUSTUM_SIZE * aspect;
        const baseFrustumHeight = BASE_FRUSTUM_SIZE;
        const sphereDiameter = sphere.radius * 2 * ADDITIONAL_RADIUS_COEFFICIENT;
        const zoomX = baseFrustumWidth / sphereDiameter;
        const zoomY = baseFrustumHeight / sphereDiameter;
        const targetZoom = Math.max(this.orbitControls.minZoom || 0.05, Math.min(Math.min(zoomX, zoomY), this.orbitControls.maxZoom || 50));
        const direction = new Vector3();
        this.camera.getWorldDirection(direction);
        const distance = this.camera.position.distanceTo(this.orbitControls.target) || 50;
        const newPosition = sphere.center.clone().sub(direction.multiplyScalar(distance));
        const newUp = this.camera.up.clone();
        const newTarget = sphere.center.clone();
        this.cameraAnimator.tweenCameraTo(newPosition, newUp, newTarget, {
            zoom: targetZoom,
        });
    }
    zoomToFit() {
        const boundingSphere = this.getSceneBoundingSphere([isPart3D]);
        if (boundingSphere.isEmpty()) {
            return;
        }
        this.zoomToSphere(boundingSphere);
    }
    zoomToObjects(objects) {
        if (objects.length === 0) {
            return;
        }
        const boundingSphere = this.getSceneBoundingSphere([(obj) => objects.includes(obj)]);
        if (boundingSphere.isEmpty()) {
            return;
        }
        this.zoomToSphere(boundingSphere);
    }
    zoomToSelectedOrToFit() {
        this.zoomToFit();
    }
    setCameraView(view) {
        const target = this.orbitControls.target.clone();
        const distance = this.camera.position.distanceTo(target);
        const offset = view.offset.clone().normalize();
        const newPosition = target.clone().add(offset.multiplyScalar(distance));
        const newUp = view.up.clone();
        this.cameraAnimator.tweenCameraTo(newPosition, newUp, target);
    }
    isSceneEmpty() {
        const sceneBoundingBox = this.getSceneBoundingBox([isPart3D]);
        return sceneBoundingBox.isEmpty();
    }
    getCameraMatrices() {
        return {
            matrixWorld: this.camera.matrixWorld.elements.slice(),
            matrixWorldInverse: this.camera.matrixWorldInverse.elements.slice(),
        };
    }
    getTransformMode() {
        return this.transformControls.mode;
    }
    setTransformMode(mode) {
        this.transformControls.setMode(mode);
    }
    setObjectDisplayMode(mode) {
        const changedObjects = [];
        const selectedObjects = [];
        if (selectedObjects.length) {
            selectedObjects.forEach((object) => {
                if (isPart3D(object)) {
                    object.setDisplayMode(mode, true);
                    changedObjects.push(object);
                }
                else {
                    const parent = this.getRootObject3D(object);
                    if (isPart3D(parent)) {
                        parent === null || parent === void 0 ? void 0 : parent.setChildDisplayMode(object, mode);
                    }
                }
            });
        }
        else {
            this.scene.traverse((child) => {
                if (isPart3D(child)) {
                    child.setDisplayMode(mode, true);
                    changedObjects.push(child);
                }
            });
        }
        if (changedObjects.length > 0) {
            this.emit(ViewerEvents.ObjectDisplayModeChanged, {
                mode,
                objects: changedObjects.map((o) => o.uuid),
            });
        }
    }
    getRootObject3D(object) {
        let current = object;
        while (current.parent && !current.hasOwnProperty('meshes')) {
            current = current.parent;
        }
        return current;
    }
    getObjectDisplayMode(uuid) {
        const object = this.getObjectByUUID(uuid);
        if (isPart3D(object)) {
            return object.getDisplayMode();
        }
        else if (object) {
            const parent = this.getRootObject3D(object);
            if (isPart3D(parent)) {
                return parent === null || parent === void 0 ? void 0 : parent.getChildDisplayMode(object);
            }
        }
        return null;
    }
    toggleDisplayMode() {
        const selectedObjects = [];
        if (selectedObjects.length) {
            if (selectedObjects.every((object) => isPart3D(object) && object.getDisplayMode() === ObjectDisplayMode.Normal)) {
                selectedObjects.forEach((object) => isPart3D(object) && object.setDisplayMode(ObjectDisplayMode.WireFrame));
            }
            else {
                selectedObjects.forEach((object) => isPart3D(object) && object.setDisplayMode(ObjectDisplayMode.Normal));
            }
        }
        else {
            const allObjects = [];
            this.scene.traverse((child) => {
                if (isPart3D(child)) {
                    allObjects.push(child);
                }
            });
            if (allObjects.every((obj) => obj.getDisplayMode() === ObjectDisplayMode.Normal)) {
                allObjects.forEach((object) => object.setDisplayMode(ObjectDisplayMode.WireFrame));
            }
            else {
                allObjects.forEach((object) => object.setDisplayMode(ObjectDisplayMode.Normal));
            }
        }
    }
    loadModels(files_1) {
        return __awaiter(this, arguments, void 0, function* (files, meta = [], materialFile) {
            const loader = new Loader();
            const addedObjects = [];
            const materialFileUrl = materialFile ? URL.createObjectURL(materialFile) : undefined;
            for (let i = 0; i < files.length; i++) {
                const url = URL.createObjectURL(files[i].blob);
                const object = yield loader.loadFileByURL(url, files[i].name, materialFileUrl);
                if (object) {
                    const { id, name } = files[i];
                    object.name = name;
                    object.uuid = id;
                    object.traverse((child) => {
                        child.userData.uuid = generateUUIDFromGeometryAndHierarchy(id, child);
                    });
                    addedObjects.push(object);
                }
                URL.revokeObjectURL(url);
                if (materialFileUrl) {
                    URL.revokeObjectURL(materialFileUrl);
                }
            }
            function processMeta(mMeta, parent, sub = false) {
                const object = sub
                    ? parent.children.find((o) => o.userData.uuid === mMeta.id)
                    : addedObjects.find((o) => o.userData.uuid === mMeta.id);
                if (object) {
                    mMeta.children.forEach((child) => processMeta(child, object, true));
                    object.applyMatrix4(new Matrix4().fromArray(mMeta.matrix));
                    parent.add(object);
                }
            }
            if (meta.length) {
                meta.forEach((metaObject) => processMeta(metaObject, this.scene));
                this.emit(ViewerEvents.AddObjects, addedObjects.map((object) => object.uuid));
            }
            else {
                this.execute(new AddObjectCommand(addedObjects, this));
            }
        });
    }
    getProjectMeta() {
        function processObject(object) {
            object.updateMatrix();
            const isDefaultMatrix = object.matrix.equals(new Matrix4());
            const skipNode = isDefaultMatrix && object.children.length === 0;
            if (skipNode) {
                return;
            }
            return {
                id: object.userData.uuid,
                matrix: object.matrix.toArray(),
                children: object.children.map((o) => processObject(o)).filter(res => !!res),
            };
        }
        return this.scene.children.filter(isPart3D).map((o) => processObject(o)).filter(res => !!res);
    }
    getSceneGraph() {
        function traverseObject(object) {
            var _a;
            const children = object.children
                .filter((obj) => !isLineSegments(obj) && (isMesh(obj) || isObject3D(obj) || isGroup(obj)))
                .map((child) => traverseObject(child));
            return {
                id: object.uuid,
                name: object.name,
                visible: (_a = object.userData.isVisible) !== null && _a !== void 0 ? _a : object.visible,
                children: children
            };
        }
        return this.scene.children.filter(isPart3D).map(child => traverseObject(child));
    }
    clear() {
        this.scene.children = this.scene.children.filter((child) => !isPart3D(child));
        this.transformControls.detach();
        this.emit(ViewerEvents.Cleared, true);
    }
    getScene() {
        return this.scene;
    }
    getCamera() {
        return this.camera;
    }
    getControls() {
        return this.orbitControls;
    }
    getSelectionManager() {
        return {};
        // return this.selectionManager;
    }
    /**
     * Computes bounding box of the scene
     * @param filters array of predicate functions. Allows to compute scene bounding box using only objects that satisfy all predicates in this array
     * @returns bounding box of the scene
     */
    getSceneBoundingBox(filters = []) {
        const box = new Box3();
        this.scene.traverse((child) => {
            if (filters.map((filter) => filter(child)).every(value => value)) {
                box.expandByObject(child);
            }
        });
        return box;
    }
    /**
     *
     * @param filters
     * @returns bounding sphere of scene bounding box
     */
    getSceneBoundingSphere(filters = []) {
        return this.getSceneBoundingBox(filters).getBoundingSphere(new Sphere());
    }
    render() {
        this.renderer.render(this.scene, this.camera);
    }
    animate() {
        this.animationFrameRequestID = requestAnimationFrame(this.animate.bind(this));
        this.orbitControls.update();
        this.render();
    }
    stopAnimate() {
        cancelAnimationFrame(this.animationFrameRequestID);
    }
    execute(cmd, optionalName) {
        this.history.execute(cmd, optionalName);
    }
    undo() {
        this.history.undo();
    }
    redo() {
        this.history.redo();
    }
    onHistoryChanged() {
        this.updateCameraFarPlaneForScene();
        if (this.isSceneEmpty()) {
            this.grid.reset();
            return;
        }
        this.grid.onObjectTransformed();
    }
    getUndosCount() {
        return this.history.getUndos().length;
    }
    getRedosCount() {
        return this.history.getRedos().length;
    }
    getHistory() {
        return this.history;
    }
    addObjects(objects, parentInfos = {}) {
        objects.forEach((object) => {
            const parentInfo = parentInfos[object.uuid];
            if (parentInfo) {
                parentInfo.parent.children.splice(parentInfo.index, 0, object);
                object.parent = parentInfo.parent;
            }
            else {
                const sceneBoundingBox = this.getSceneBoundingBox([isPart3D]);
                if (!sceneBoundingBox.isEmpty()) {
                    const box = new Box3().setFromObject(object);
                    const halfWidth = (box.max.x - box.min.x) / 2;
                    object.position.set(sceneBoundingBox.max.x + Math.abs(halfWidth) + DEFAULT_NEW_MODEL_SHIFT, 0, 0);
                }
                this.scene.add(object);
            }
        });
        this.updateCameraFarPlaneForScene();
        this.emit(ViewerEvents.AddObjects, objects.map((object) => object.uuid));
    }
    removeObjects(...objects) {
        objects.forEach((object) => {
            if (object.parent === null) {
                return;
            }
            object.parent.remove(object);
        });
        this.updateCameraFarPlaneForScene();
        this.emit(ViewerEvents.RemoveObjects, objects.map((object) => object.uuid));
        this.transformControls.detach();
    }
    updateCameraFarPlaneForScene() {
        let maxDistance = DEFAULT_DISTANCE;
        const sceneBoundingBox = this.getSceneBoundingBox([isPart3D]);
        if (!sceneBoundingBox.isEmpty()) {
            const farthestPoint = sceneBoundingBox.max;
            const distanceToFarthestPoint = this.scene.position.distanceTo(farthestPoint);
            maxDistance = Math.max(maxDistance, distanceToFarthestPoint);
        }
        else {
            this.grid.reset();
        }
        this.camera.far = maxDistance * FAR_MULTIPLIER;
        this.camera.updateProjectionMatrix();
        this.orbitControls.maxDistance = maxDistance * ZOOM_MULTIPLIER;
    }
    getRendererDomElement(canvasId) {
        return document.getElementById(canvasId);
    }
    getCanvasDomElement() {
        return this.renderer.domElement;
    }
    getCanvasParentDomElement() {
        return this.canvasParent;
    }
    emitCameraChangedEvent() {
        this.camera.updateMatrixWorld();
        this.emit(ViewerEvents.CameraChanged, this.getCameraMatrices());
    }
    togglePartVisibility(objectUUID) {
        var _a, _b;
        const object = this.getObjectByUUID(objectUUID);
        if (!object) {
            return;
        }
        const isVisible = (_b = (_a = object.userData) === null || _a === void 0 ? void 0 : _a.isVisible) !== null && _b !== void 0 ? _b : object.visible;
        this.execute(new ChangeVisibilityCommand([object], [!isVisible], this));
    }
    toggleSelectedPartsVisibility() {
        // No selection manager - method kept for API compatibility
    }
    onResize() {
        const width = this.canvasParent.offsetWidth;
        const height = this.canvasParent.offsetHeight;
        if (width === 0 || height === 0)
            return;
        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        const aspect = width / height;
        this.camera.left = (-BASE_FRUSTUM_SIZE * aspect) / 2;
        this.camera.right = (BASE_FRUSTUM_SIZE * aspect) / 2;
        this.camera.top = BASE_FRUSTUM_SIZE / 2;
        this.camera.bottom = -BASE_FRUSTUM_SIZE / 2;
        this.camera.updateProjectionMatrix();
    }
    onKeyDown(event) {
        if (['input', 'textarea'].includes(event.target.tagName.toLowerCase())) {
            return;
        }
        switch (event.code) {
            case 'Escape':
                this.transformControls.detach();
                break;
            case 'KeyR':
                if (this.transformControls.enabled) {
                    this.transformControls.setMode('rotate');
                }
                break;
            case 'KeyT':
                if (this.transformControls.enabled) {
                    this.transformControls.setMode('translate');
                }
                break;
            case 'KeyS':
                if (this.transformControls.enabled) {
                    this.transformControls.setMode('scale');
                }
                break;
            case 'KeyZ':
                if (event.ctrlKey || event.metaKey) {
                    if (!['input', 'textarea'].includes(event.target.tagName.toLowerCase())) {
                        this.undo();
                    }
                }
                break;
            case 'KeyY':
                if (!['input', 'textarea'].includes(event.target.tagName.toLowerCase())) {
                    this.redo();
                }
                break;
            case 'KeyW':
                this.toggleDisplayMode();
                break;
            case 'KeyH':
                this.toggleSelectedPartsVisibility();
                break;
            case 'Delete':
                this.transformControls.detach();
                // No selection manager - delete key does nothing
                break;
            default:
                break;
        }
    }
}
